/**
 * QMS role-permissions matrix, encoded as DATA.
 *
 * This is the single consumable realization of `01-role-permissions-matrix.md`
 * (§2 authority floor, §3 the matrix, §4 scope tokens). It is consumed by:
 *   - 04-03 (the scope-filter engine) — reads `ROLE_REGISTER_PERMISSIONS[role]`
 *     `.defaultScope` and the `SCOPE_TOKEN` semantics to build the per-register
 *     filter over `AnyRegisterRecord`.
 *   - 04-05 / 04-06 / 04-07 (tiles / lodge / register views) — reads `actions`
 *     to gate UI (show the lodge form, the investigate affordance, etc).
 *
 * ---------------------------------------------------------------------------
 * AUTHORITY FLOOR (matrix §2 — non-negotiable, encoded as data here)
 * ---------------------------------------------------------------------------
 *   - Agents/automation/the dashboard assist & draft only. No role carries an
 *     auto-`approve` or auto-`close` action. The dashboard-actionable verb set
 *     is therefore deliberately `lodge | view | investigate` ONLY — there is no
 *     `approve` / `close` action a role can hold (see `RegisterAction`).
 *   - The matrix's `Approve` column (Andy = operational, Batra = clinical /
 *     regulatory / SIRS) is HUMAN authority, recorded as the NON-MUTATING
 *     descriptor `approvalAuthority` for display/routing only. It is NOT a verb
 *     the dashboard can execute. Approval/closure remains a human act performed
 *     in the system of record, never an automated dashboard mutation.
 * ---------------------------------------------------------------------------
 */

import type { AppRole } from '../types/auth';
import type { RegisterKey } from '../types/qms';

// ---------------------------------------------------------------------------
// Scope tokens (matrix §4) — the view-scope vocabulary the scope engine renders
// ---------------------------------------------------------------------------

/**
 * The default-view scope token for a (role, register) pair (matrix §4).
 * Each token is a concrete filter intent the 04-03 scope engine realizes over
 * `AnyRegisterRecord`. **Exported for 04-03 to import and switch on.**
 *
 *  - `Own only`        — items the user lodged. Realize via `reportedByEmail == [Me]`
 *                        (Incidents) else `createdBy == [Me]` (matrix §4).
 *  - `Portfolio`       — Care Manager's managed business units. Realize via
 *                        `managingUnit ∈ {CM's BU set}` (fallback `businessUnit`
 *                        where `managingUnit` blank). Until the per-CM BU map is
 *                        confirmed (04-04-rbac-resolved.md §1), the build default
 *                        is a single `managingUnit`-grouped view any CM filters.
 *  - `Finance-relevant`— items with a financial dimension. Realize via the
 *                        finance-relevant `category` subset (04-04-rbac-resolved
 *                        §4) until a native `FinanceRelevant` flag lands.
 *  - `HR/WHS-touching` — worker-safety / HR items. Realize via
 *                        `businessUnit == 'Staff' OR category ∈ {worker-safety
 *                        subset}` (04-04-rbac-resolved §4).
 *  - `All`             — full oversight read, no filter (matrix §4).
 */
export type ScopeToken =
  | 'Own only'
  | 'Portfolio'
  | 'Finance-relevant'
  | 'HR/WHS-touching'
  | 'All';

/** All scope tokens, for exhaustive switch validation in 04-03. */
export const SCOPE_TOKENS: readonly ScopeToken[] = [
  'Own only',
  'Portfolio',
  'Finance-relevant',
  'HR/WHS-touching',
  'All',
] as const;

// ---------------------------------------------------------------------------
// Dashboard-actionable verbs (authority floor — NO approve / close)
// ---------------------------------------------------------------------------

/**
 * The verbs the dashboard can gate / execute. Deliberately excludes
 * `approve` and `close` (authority floor §2): approval/closure is a human act
 * in the system of record, never an automated dashboard mutation.
 *
 *  - `lodge`       — create a new record in this register.
 *  - `view`        — read this register (scoped by `defaultScope`).
 *  - `investigate` — record investigation steps / actions (glass-box audit
 *                    sibling), within the role's scope. NOT approval.
 */
export type RegisterAction = 'lodge' | 'view' | 'investigate';

/** All actions, for UI gating helpers. */
export const REGISTER_ACTIONS: readonly RegisterAction[] = [
  'lodge',
  'view',
  'investigate',
] as const;

/**
 * Human approval authority (matrix §2 / §3 `Approve` column). This is a
 * NON-MUTATING descriptor — recorded for display/routing only. It is explicitly
 * NOT a `RegisterAction`, so no role can carry an auto-approve/close. Approval
 * is always a human act performed in the system of record.
 *
 *  - `none`        — no approval authority (most roles → escalate to a human).
 *  - `operational` — Andy: approves operational matters (still a human act).
 *  - `clinical`    — Batra: final clinical / regulatory / SIRS authority,
 *                    including any external notification (still a human act).
 */
export type ApprovalAuthority = 'none' | 'operational' | 'clinical';

// ---------------------------------------------------------------------------
// Per-register permission cell
// ---------------------------------------------------------------------------

/** One (role, register) cell: what the role may DO + its default view scope. */
export interface RegisterPermission {
  /** Dashboard-actionable verbs the role holds for this register. */
  actions: readonly RegisterAction[];
  /** The default view scope token applied when the role opens this register. */
  defaultScope: ScopeToken;
}

/** A role's permission set across every register, plus its approval authority. */
export interface RolePermissions {
  /**
   * Human approval authority (NON-MUTATING — see `ApprovalAuthority`). Recorded
   * so the UI can show "→ Batra" / "→ Andy" routing; never an auto-action.
   */
  approvalAuthority: ApprovalAuthority;
  /** Per-register permission cells (one entry per `RegisterKey`). */
  registers: Record<RegisterKey, RegisterPermission>;
}

// ---------------------------------------------------------------------------
// Helper builders (keep the matrix below readable)
// ---------------------------------------------------------------------------

/** Build a uniform per-register map (same cell for all five registers). */
function uniformRegisters(
  actions: readonly RegisterAction[],
  defaultScope: ScopeToken,
): Record<RegisterKey, RegisterPermission> {
  const cell: RegisterPermission = { actions, defaultScope };
  return {
    incidents: cell,
    complaints: cell,
    ci: cell,
    feedback: cell,
    risk: cell,
  };
}

/**
 * Build the per-register map for the non-admin field reporters (matrix §3 rows
 * Support Worker / NDIS Subcontractor / Allied Professional — identical):
 *  - Lodge Incident: Yes · Lodge Complaint: Yes · Lodge CI: **No** ·
 *    Lodge Feedback: Yes
 *  - View: `Own only` everywhere. No investigate. No approve.
 *
 * CI lodging is denied (matrix §3 rationale: CI is an owned management queue;
 * non-admin observations enter as Feedback / Incident / Complaint and a Care
 * Manager converts them). So the CI cell is **view-only** (`Own only`), no lodge.
 */
function fieldReporterRegisters(): Record<RegisterKey, RegisterPermission> {
  return {
    incidents: { actions: ['lodge', 'view'], defaultScope: 'Own only' },
    complaints: { actions: ['lodge', 'view'], defaultScope: 'Own only' },
    ci: { actions: ['view'], defaultScope: 'Own only' }, // Lodge CI = No (§3)
    feedback: { actions: ['lodge', 'view'], defaultScope: 'Own only' },
    risk: { actions: ['view'], defaultScope: 'Own only' },
  };
}

// ---------------------------------------------------------------------------
// THE MATRIX, AS DATA (matrix §3 collapsed onto the §5 App Role identity map)
// ---------------------------------------------------------------------------

/**
 * Role → permissions, per register, per the matrix. The four OFFICE roles
 * (Oversight / CareManager / HRWHS / Finance) drive the Phase-4 admin shell;
 * the three field-reporter roles are modelled for 04-03 scope but are denied
 * the admin shell (App.tsx `OFFICE_ROLES` guard).
 *
 * NOTE: no `actions` array contains `approve` or `close` — those are not in the
 * `RegisterAction` union (authority floor §2). Approval authority lives in the
 * separate non-mutating `approvalAuthority` field.
 */
export const ROLE_REGISTER_PERMISSIONS: Record<AppRole, RolePermissions> = {
  // --- Office roles -------------------------------------------------------

  /**
   * Oversight — Andy (Operations), Batra (Director/RN), QSO (Prathyusha).
   * Matrix: lodge everything · View `All` · oversight investigate · Approve
   * (HUMAN-only, recorded as `approvalAuthority`, NOT an action).
   * Note: Andy = operational approver, Batra = clinical/regulatory/SIRS approver.
   * Both map to the `Oversight` App Role; the highest authority on this shared
   * role is recorded as `clinical` so the UI surfaces final-authority routing.
   * Per-person operational-vs-clinical distinction is a display concern, not an
   * access gate (both hold `All` read + oversight investigate).
   */
  Oversight: {
    approvalAuthority: 'clinical',
    registers: uniformRegisters(['lodge', 'view', 'investigate'], 'All'),
  },

  /**
   * CareManager — matrix: lodge everything · View `Portfolio` · investigate
   * (own portfolio) · Approve **No** (→ Batra). Default scope `Portfolio`.
   */
  CareManager: {
    approvalAuthority: 'none',
    registers: uniformRegisters(['lodge', 'view', 'investigate'], 'Portfolio'),
  },

  /**
   * HRWHS — Amran. Matrix: lodge everything · View `HR/WHS-touching` ·
   * investigate (HR/WHS angle) · Approve **No** (→ Batra / Andy).
   */
  HRWHS: {
    approvalAuthority: 'none',
    registers: uniformRegisters(
      ['lodge', 'view', 'investigate'],
      'HR/WHS-touching',
    ),
  },

  /**
   * Finance — Amanda. Matrix: lodge everything · View `Finance-relevant` ·
   * investigate **No** · Approve **No**. View + lodge only.
   */
  Finance: {
    approvalAuthority: 'none',
    registers: uniformRegisters(['lodge', 'view'], 'Finance-relevant'),
  },

  // --- Field-reporter roles (Phase-5 surface; denied the admin shell) -----

  SupportWorker: {
    approvalAuthority: 'none',
    registers: fieldReporterRegisters(),
  },
  NDISSubcontractor: {
    approvalAuthority: 'none',
    registers: fieldReporterRegisters(),
  },
  AlliedProfessional: {
    approvalAuthority: 'none',
    registers: fieldReporterRegisters(),
  },
};

// ---------------------------------------------------------------------------
// Consumer helpers (04-03 scope + 04-05/06/07 UI gating)
// ---------------------------------------------------------------------------

/**
 * The default view scope token for a role on a register (matrix §4). When a
 * login carries multiple roles, the BROADEST scope wins (Oversight `All`
 * dominates). 04-03 imports this to seed its filter.
 */
export function defaultScopeFor(
  roles: AppRole[],
  register: RegisterKey,
): ScopeToken | null {
  const scopes = roles
    .map((r) => ROLE_REGISTER_PERMISSIONS[r]?.registers[register]?.defaultScope)
    .filter((s): s is ScopeToken => Boolean(s));
  if (scopes.length === 0) return null;
  // Breadth order: All ⊃ HR/WHS-touching | Finance-relevant | Portfolio ⊃ Own only
  const breadth: Record<ScopeToken, number> = {
    All: 4,
    'HR/WHS-touching': 3,
    'Finance-relevant': 3,
    Portfolio: 2,
    'Own only': 1,
  };
  return scopes.reduce((a, b) => (breadth[b] > breadth[a] ? b : a));
}

/**
 * Whether ANY of the login's roles holds `action` on `register` (UI gating).
 * Used by 04-06 (lodge) / 04-07 (views) to show/hide affordances.
 */
export function canDo(
  roles: AppRole[],
  register: RegisterKey,
  action: RegisterAction,
): boolean {
  return roles.some((r) =>
    ROLE_REGISTER_PERMISSIONS[r]?.registers[register]?.actions.includes(action),
  );
}

/**
 * The highest human approval authority across a login's roles (NON-MUTATING).
 * For display/routing only ("→ Batra"); never gates a dashboard mutation.
 */
export function approvalAuthorityFor(roles: AppRole[]): ApprovalAuthority {
  const order: Record<ApprovalAuthority, number> = {
    none: 0,
    operational: 1,
    clinical: 2,
  };
  return roles
    .map((r) => ROLE_REGISTER_PERMISSIONS[r]?.approvalAuthority ?? 'none')
    .reduce<ApprovalAuthority>(
      (a, b) => (order[b] > order[a] ? b : a),
      'none',
    );
}
